<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'
import useSocket from '@/composables/useSocket'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'

defineProps<{
  timeRemaining?: number
}>()

const emit = defineEmits<{
  timeUp: []
}>()

const { isConnected, isReconnecting, reconnectAttempts, lastError, connect, hasSetupConnection } = useSocket()
const { isNetworkAbsent } = storeToRefs(useAppStore())

// Local derived state for browser offline (network adapter down)
const isBrowserOffline = ref(!navigator.onLine)

const isProduction = import.meta.env.PROD

const handleOnline = () => {
  isBrowserOffline.value = false
  // If the socket is disconnected, attempt a reconnect
  if (!isConnected) connect()
}
const handleOffline = () => {
  isBrowserOffline.value = true
}

const currentTime = ref<number | undefined>(undefined)
let intervalId: number | null = null

const formattedMinutes = computed(() => {
  if (!currentTime.value) return '00'
  const minutes = Math.floor(currentTime.value / 60)
  return minutes.toString().padStart(2, '0')
})

const formattedSeconds = computed(() => {
  if (!currentTime.value) return '00'
  const seconds = currentTime.value % 60
  return seconds.toString().padStart(2, '0')
})

const OFFLINE_GRACE_SECONDS = 60

const startCountdown = (seconds: number) => {
  if (intervalId) clearInterval(intervalId)
  currentTime.value = seconds

  intervalId = setInterval(() => {
    if (currentTime.value && currentTime.value > 0) {
      currentTime.value--
    } else {
      clearInterval(intervalId!)
      intervalId = null
      emit('timeUp')
    }
  }, 1000)
}

const stopCountdown = () => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
}

// When the browser goes offline, start a fixed 60s grace countdown.
// When it comes back online, stop and reset it.
watch(isBrowserOffline, val => {
  if (val) {
    isNetworkAbsent.value = true
    startCountdown(OFFLINE_GRACE_SECONDS)
  } else {
    isNetworkAbsent.value = false
    stopCountdown()
    currentTime.value = undefined
  }
})

onMounted(() => {
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
})
onBeforeUnmount(() => {
  stopCountdown()
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
})
</script>

<template>
  <transition name="fade">
    <div v-if="hasSetupConnection && (!isConnected || isReconnecting || isBrowserOffline)" class="fixed bottom-4 left-1/2 -translate-x-1/2 z-52">
      <div
        class="flex items-center gap-3 px-4 py-2 rounded-lg shadow-lg border text-sm"
        :class="[isBrowserOffline ? 'bg-red-600/95 border-red-700 text-white' : !isConnected || isReconnecting ? 'bg-yellow-500/95 border-yellow-600 text-black' : 'bg-green-600/90 border-green-700 text-white']"
      >
        <span v-if="isBrowserOffline">You're offline. Reconnect within {{ formattedMinutes }}:{{ formattedSeconds }} to rejoin the game.</span>
        <span v-else-if="isReconnecting">Reconnecting… ({{ reconnectAttempts }})</span>
        <span v-else-if="!isConnected">Disconnected</span>
        <span v-else>Connected</span>

        <span v-if="lastError && !isBrowserOffline" class="opacity-80">— {{ isProduction ? 'An error occurred' : lastError }}</span>

        <button v-if="!isBrowserOffline && !isConnected" class="ml-2 px-2 py-1 rounded border border-black/20 bg-white/80 hover:bg-white" @click="connect">Retry</button>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
